import type { AstNode } from "langium";
import { AbstractSemanticTokenProvider, type SemanticTokenAcceptor } from "langium/lsp";
import { SemanticTokenTypes } from "vscode-languageserver";

import {
	isAlias,
	isDirective,
	isEndpoint,
	isEnum,
	isException,
	isImport,
	isImportMember,
	isLiteralValue,
	isModule,
	isReferenceDirective,
	isReferenceType,
	isScalar,
	isStruct,
} from "../generated/ast.js";
import type {
	Alias,
	Directive,
	Endpoint,
	Enum,
	Exception,
	Import,
	ImportMember,
	LiteralValue,
	Module,
	ReferenceDirective,
	ReferenceType,
	Scalar,
	Struct,
} from "../generated/ast.js";

export class IprotoSemanticTokenProvider extends AbstractSemanticTokenProvider {
	private highlightModule(node: Module, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.namespace });
	}

	private highlightImport(node: Import, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "target", type: SemanticTokenTypes.namespace });
		acceptor({ node, keyword: "::", type: SemanticTokenTypes.operator });
	}

	private highlightImportMember(node: ImportMember, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "target", type: SemanticTokenTypes.variable });
		acceptor({ node, property: "name", type: SemanticTokenTypes.type });
	}

	private highlightDirective(node: Directive, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.decorator });
		acceptor({ node, property: "locations", type: SemanticTokenTypes.macro });

		for (const param of node.parameters) {
			acceptor({ node: param, property: "name", type: SemanticTokenTypes.parameter });
			acceptor({ node: param, property: "type", type: SemanticTokenTypes.macro });
			acceptor({ node: param, keyword: "?", type: SemanticTokenTypes.operator });
			acceptor({ node: param, keyword: ":", type: SemanticTokenTypes.operator });
		}
	}

	private highlightScalar(node: Scalar, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.interface });
		acceptor({ node, keyword: ".", type: SemanticTokenTypes.operator });
		for (const option of node.options) {
			acceptor({ node: option, property: "name", type: SemanticTokenTypes.parameter });
			acceptor({ node: option, property: "type", type: SemanticTokenTypes.macro });
			acceptor({ node: option, keyword: ":", type: SemanticTokenTypes.operator });
		}
		for (const param of node.parameters) {
			acceptor({ node: param, property: "name", type: SemanticTokenTypes.type });
		}
	}

	private highlightEnum(node: Enum, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.enum });

		for (const member of node.members) {
			acceptor({ node: member, property: "name", type: SemanticTokenTypes.enumMember });
		}
	}

	private highlightStruct(node: Struct, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.struct });

		for (const property of node.fields) {
			acceptor({ node: property, property: "name", type: SemanticTokenTypes.property });
		}
	}

	private highlightAlias(node: Alias, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.type });
	}

	private highlightEndpoint(node: Endpoint, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.method });
	}

	private highlightReferenceType(node: ReferenceType, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "target", type: SemanticTokenTypes.type });

		acceptor({ node: node, keyword: ".", type: SemanticTokenTypes.operator });

		for (const option of node.options) {
			acceptor({ node: option, property: "target", type: SemanticTokenTypes.parameter });
			acceptor({ node: option, keyword: ":", type: SemanticTokenTypes.operator });
		}
	}

	private highlightReferenceDirective(node: ReferenceDirective, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "target", type: SemanticTokenTypes.decorator });
		acceptor({ node, keyword: "#", type: SemanticTokenTypes.operator });

		for (const argument of node.arguments) {
			acceptor({ node: argument, keyword: ":", type: SemanticTokenTypes.operator });
			acceptor({ node: argument, property: "target", type: SemanticTokenTypes.parameter });
		}
	}

	private highlightLiteralValue(node: LiteralValue, acceptor: SemanticTokenAcceptor) {
		if (typeof node.value === "boolean") {
			acceptor({ node, property: "value", type: SemanticTokenTypes.keyword });
		}
		if (typeof node.value === "number") {
			acceptor({ node, property: "value", type: SemanticTokenTypes.number });
		}
		if (typeof node.value === "string") {
			acceptor({ node, property: "value", type: SemanticTokenTypes.string });
		}
	}

	private highlightException(node: Exception, acceptor: SemanticTokenAcceptor) {
		acceptor({ node, property: "name", type: SemanticTokenTypes.interface });
	}

	protected override highlightElement(node: AstNode, acceptor: SemanticTokenAcceptor): void | undefined | "prune" {
		if (isAlias(node)) {
			this.highlightAlias(node, acceptor);
		}

		if (isModule(node)) {
			this.highlightModule(node, acceptor);
		}

		if (isImport(node)) {
			this.highlightImport(node, acceptor);
		}

		if (isImportMember(node)) {
			this.highlightImportMember(node, acceptor);
		}

		if (isDirective(node)) {
			this.highlightDirective(node, acceptor);
		}

		if (isScalar(node)) {
			this.highlightScalar(node, acceptor);
		}

		if (isEnum(node)) {
			this.highlightEnum(node, acceptor);
		}

		if (isStruct(node)) {
			this.highlightStruct(node, acceptor);
		}

		if (isEndpoint(node)) {
			this.highlightEndpoint(node, acceptor);
		}

		if (isException(node)) {
			this.highlightException(node, acceptor);
		}

		if (isReferenceType(node)) {
			this.highlightReferenceType(node, acceptor);
		}

		if (isReferenceDirective(node)) {
			this.highlightReferenceDirective(node, acceptor);
		}

		if (isLiteralValue(node)) {
			this.highlightLiteralValue(node, acceptor);
		}
	}
}
